/**
 * Network protocol types — client ↔ game-server messages.
 *
 * Sprint 0.3: input and snapshot shapes wired up. Note: mouse deltas are
 * in DEGREES (not radians) to match server-side simple math.
 *
 * Sprint 0.7: add JWT envelope, action flags decoder, signed match results.
 */

export const PROTOCOL_VERSION = 1;

/** Bit flags packed into PlayerInputFrame.actionFlags. */
export const ACTION = {
  SHOOT:        1 << 0,
  RELOAD:       1 << 1,
  SWITCH_PRIM:  1 << 2,
  SWITCH_SEC:   1 << 3,
  SWITCH_MELEE: 1 << 4,
  SWITCH_GREN:  1 << 5,
  THROW_GREN:   1 << 6,
  ZOOM:         1 << 7,
  /** Edge-triggered: knife LMB slash (fast, low damage). */
  KNIFE_SLASH:  1 << 8,
  /** Edge-triggered: knife RMB stab (slow, high damage, one-shot back). */
  KNIFE_STAB:   1 << 9,
} as const;

/**
 * One frame of player input sent to the game-server every tick.
 * Sequence number drives lag-compensation reconciliation in Phase 1.
 */
export interface PlayerInputFrame {
  seq: number;
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  /**
   * Hold to crouch — slower move, lower camera / shot origin (see `MOVEMENT_FPS`).
   * **Future:** weapon spread should use `MOVEMENT_FPS.CROUCH_ACCURACY_SPREAD_MULT` for tighter shots.
   */
  crouch: boolean;
  /** Shift + movement: slower walk; footstep layer should skip run SFX (see MOVEMENT_FPS.WALK_SPEED_FACTOR). */
  walk: boolean;
  /**
   * Absolute look angles this frame in DEGREES (client-authoritative aim).
   * The server sets player.yaw/pitch directly from these, so a client/server
   * yaw-base mismatch — and thus the post-respawn move/aim desync — cannot
   * occur. Optional for legacy clients that only send the deltas below.
   */
  yaw?: number;
  pitch?: number;
  /** Mouse delta this frame in DEGREES. LEGACY: integrated by the server only
   *  when absolute yaw/pitch are absent (deploy-skew fallback). */
  dPitch: number;
  dYaw: number;
  actionFlags: number;
}

/** Per-player snapshot — matches PlayerState schema fields. */
export interface PlayerSnapshot {
  sessionId: string;
  name: string;
  x: number; y: number; z: number;
  yaw: number; pitch: number;
  health: number;
  weaponId: string;
  lastProcessedInputSeq: number;
  /** Spawn side for MR13 scoreboard colouring. */
  team?: "ct" | "t";
  /** Match economy (authoritative dollars). */
  money?: number;
  /** Weapon ids owned this match (server economy). */
  ownedWeapons?: string[];
}

/** Match lifecycle phases (server is source of truth). */
export type MatchPhase = "warmup" | "buy" | "live" | "between_rounds" | "end";

/** Top-level server snapshot — matches ArenaState schema + match HUD fields. */
export interface ServerSnapshot {
  tick: number;
  phase: MatchPhase;
  players: PlayerSnapshot[];
  /** CT side total round wins. */
  ctScore?: number;
  /** T side total round wins. */
  tScore?: number;
  /** 1-based round index currently in play (or next, after increment — see server). */
  roundNumber?: number;
  /** Wins required to take the match (MR12 = 13). */
  roundsToWin?: number;
  /** When `phase === "buy"`, server tick at which buy ends (exclusive). */
  buyEndsAtTick?: number;
  /** Seconds remaining in buy phase (server-computed, optional). */
  buySecondsLeft?: number;
  /** Sprint 6 — seconds remaining in the live round (live phase only). */
  liveSecondsLeft?: number;
  /** Sprint 6 — round at which halftime sides swap (MR12 = 12). */
  halftimeAtRound?: number;
  /** Sprint 6 — true once halftime has triggered for this match. */
  halftimeSwapped?: boolean;
  /** True once the breakable glass pane (T-room E window) has shattered
   *  this round. Late-join / dropped-event clients converge off this. */
  glassBroken?: boolean;
}

/** Client → server: purchase a weapon during buy phase. */
export interface BuyWeaponMsg {
  weaponId: string;
}

/** Client → server: switch to an owned weapon (any non-end phase). */
export interface WeaponEquipMsg {
  weaponId: string;
}

/** Moderation reason for a player report (mirrors the API's reportsStore). */
export type ReportReason = "cheating" | "abuse" | "harassment" | "scam" | "other";

/**
 * Client → server: file a report against another player in the match. The
 * server maps both session ids to their auth-verified wallets and relays the
 * report to the API (admins review it in the dashboard). Identifying the
 * target by `sessionId` keeps wallets off the client.
 */
export interface ReportPlayerMsg {
  targetSessionId: string;
  reason: ReportReason;
  detail?: string;
}

/**
 * Client → server: send a friend request to another player in the match. The
 * server maps both session ids to wallets and relays to the social API, which
 * auto-accepts when the other player already requested you (mutual → friends).
 */
export interface FriendRequestMsg {
  targetSessionId: string;
}

/** Outcome of a friend request (mirrors the social store's SendResult). */
export type FriendRequestResult = "requested" | "accepted" | "already_friends" | "already_pending";

/**
 * Server → requesting client: the outcome of their friend request, so the UI
 * can show the right message ("request sent" vs "now friends" vs "already
 * friends") instead of always assuming a fresh request was sent.
 */
export interface FriendResultMsg {
  targetSessionId: string;
  result: FriendRequestResult;
}

/**
 * Server → client: pre-existing friendship with another seated player, pushed
 * on join (the server checks the social API) so the Tab scoreboard shows its
 * green "Friends" badge when it opens — not only after an add-friend click.
 * Only sent when the pair is friends.
 */
export interface FriendStatusMsg {
  targetSessionId: string;
  friends: boolean;
}

/**
 * Client → server: an in-match chat line (opened with T). The server is
 * authoritative over what actually reaches the other player: it trims,
 * length-caps, rate-limits and profanity-censors before broadcasting, so
 * clients never render a raw peer-supplied string.
 */
export interface ChatMsg {
  text: string;
}

/** Server → all clients: a validated + censored chat line. */
export interface ChatBroadcastMsg {
  sessionId: string;
  name: string;
  team?: "ct" | "t";
  text: string;
}

/** Server → all clients: a player equipped a weapon (authoritative). */
export interface WeaponEquipBroadcastMsg {
  sessionId: string;
  weaponId: string;
}

/** Server → all clients when a round ends (HP reached 0 or future win conditions). */
export interface RoundEndBroadcastMsg {
  tick: number;
  winnerSessionId: string;
  loserSessionId: string;
  /** Winning side this round — drives the "Counter-Terrorists / Terrorists
   *  Win" announcer (played to both players). Reflects the post-halftime
   *  side, so it tracks whoever is currently CT / T. */
  winnerTeam: "ct" | "t";
  ctScore: number;
  tScore: number;
  /** 1-based round that was just completed. */
  roundCompleted: number;
  matchOver: boolean;
  /** Set when `matchOver` is true — who won the match. */
  matchWinnerSessionId?: string | null;
}

/** Server → all clients when a shot connects (authoritative). */
export interface HitBroadcastMsg {
  tick: number;
  attackerSessionId: string;
  victimSessionId: string;
  weaponId: string;
  /** Hit zone id (matches `HitZone` in hitbox.ts, excluding miss). */
  zone: string;
  damage: number;
  victimHealthAfter: number;
  /**
   * Sprint 5 — attacker eye position at the moment of fire. Used by the
   * victim's client to compute the screen-edge directional damage arc.
   */
  attackerX?: number;
  attackerY?: number;
  attackerZ?: number;
  /**
   * Sprint 5 — world-space impact point on the victim's body (server's
   * skeletal-hit result). Drives blood spatter origin client-side.
   */
  hitX?: number;
  hitY?: number;
  hitZ?: number;
}

/**
 * Sprint 5 — server broadcasts every authoritative gun shot (hit or
 * miss) so peers can render bullet tracers + spawn world impact dust
 * via local AABB raycasts. Origin = attacker eye, dir = normalized aim
 * direction *after* spray pattern + Gaussian spread were applied
 * server-side.
 */
export interface WeaponFireBroadcastMsg {
  tick: number;
  attackerSessionId: string;
  weaponId: string;
  /** Eye origin in world coords. */
  ox: number;
  oy: number;
  oz: number;
  /** Unit-length aim direction (server post-spray/spread). */
  dx: number;
  dy: number;
  dz: number;
}

/**
 * Sprint 6 — kill feed event. Fires on every authoritative kill (any
 * weapon, including knife). `headshot` is derived from the killing
 * hit's zone server-side; `suicide` is reserved for fall damage /
 * future grenade self-kills.
 */
export interface KillBroadcastMsg {
  tick: number;
  attackerSessionId: string;
  victimSessionId: string;
  weaponId: string;
  headshot: boolean;
  wallbang?: boolean;
  suicide?: boolean;
  /** Victim was standing inside an active smoke cloud when they died. */
  throughSmoke?: boolean;
  /** Victim was still flash-blinded (from a recent flashbang) when they died. */
  blindKill?: boolean;
  /** Sniper kill taken from the hip — scope was down on the firing frame. */
  noScope?: boolean;
}

/**
 * Server → each client once when the match is decided (a side reached
 * `roundsToWin`, or the opponent forfeited by leaving). Targeted send:
 * `won` is from the recipient's perspective. Carries the on-chain pot
 * outcome so the client can render the payout and warn when no escrow
 * was locked (free play / API down / lock failed).
 */
export interface MatchEndBroadcastMsg {
  /** True for the recipient if they won the match. */
  won: boolean;
  /** Session id of the match winner. */
  winnerSessionId: string;
  /** Dollar stake per player for this match. */
  stake: number;
  /** Winner's gross payout in dollars (2*stake − fee). 0 for the loser. */
  payout: number;
  /** Platform fee in dollars (5% of the pot). */
  fee: number;
  /** True iff both players' stakes were locked on-chain (real money). */
  escrowed: boolean;
  /** Total match duration in seconds, from match start (intro) to the
   *  deciding moment. Optional so clients tolerate older servers. */
  durationSec?: number;
}

/* ─────────────────────────────────────────────────────────────────
   GRENADES — Sprint 7

   Server-authoritative grenade flight + damage + LOS. The thrower's
   client only computes the throw vector and sends `grenade_throw`;
   server simulates physics and broadcasts per-tick `grenade_state`
   and one-shot `grenade_detonate`. Smoke / fire persist via separate
   `zone_state` broadcasts so all clients render the same world.
   ───────────────────────────────────────────────────────────────── */

export type GrenadeKindWire = "he" | "flash" | "smoke" | "molotov" | "incendiary";

/** Client → server when the player releases a charged throw. */
export interface GrenadeThrowMsg {
  kind: GrenadeKindWire;
  /** Spawn position in world coords (slightly forward of camera). */
  ox: number;
  oy: number;
  oz: number;
  /** Initial velocity in m/s. */
  vx: number;
  vy: number;
  vz: number;
}

/**
 * Server → all clients, per-tick during flight. Snapshot of every
 * in-flight grenade so peers can render a marble at the right spot.
 */
export interface GrenadeStateBroadcastMsg {
  tick: number;
  grenades: Array<{
    id: number;
    kind: GrenadeKindWire;
    ownerSessionId: string;
    x: number;
    y: number;
    z: number;
  }>;
}

/**
 * Server → all clients on impact. Triggers the matching effect
 * (HE explosion / flashbang white-out / smoke spawn / molotov fire).
 */
export interface GrenadeDetonateBroadcastMsg {
  tick: number;
  id: number;
  kind: GrenadeKindWire;
  x: number;
  y: number;
  z: number;
  ownerSessionId: string;
}

/**
 * Server → all clients when an in-flight grenade bounces off the floor or
 * a wall hard enough to be audible. Drives the grenade impact ("hit")
 * sound. Emitted at most once per collision, gated on impact speed so a
 * settling grenade's micro-bounces don't spam.
 */
export interface GrenadeBounceBroadcastMsg {
  tick: number;
  id: number;
  kind: GrenadeKindWire;
  x: number;
  y: number;
  z: number;
}

/**
 * Server → all clients when the breakable glass pane (T-room E window)
 * shatters — on the first bullet that hits it while intact. Drives the
 * shatter VFX + SFX and flips the rendered pane off on every client
 * (lockstep). `x/y/z` is the world impact point on the pane.
 */
export interface GlassBreakBroadcastMsg {
  tick: number;
  x: number;
  y: number;
  z: number;
}

/**
 * Per-tick broadcast of active fire / smoke zones so clients render
 * the persistent cloud / fire patches at server-authoritative coords.
 */
export interface ZoneStateBroadcastMsg {
  tick: number;
  zones: Array<{
    id: number;
    kind: "smoke" | "fire";
    x: number;
    y: number;
    z: number;
    radius: number;
    ageSec: number;
    remainingSec: number;
  }>;
}

/**
 * Server → a specific client when a flashbang blinds THEM. Exposure is
 * computed authoritatively server-side (line-of-sight through walls + view
 * angle + distance), so the client just renders the white-out — no more
 * "flashed through a wall". Sent per-player (targeted, not broadcast); a
 * player who is fully occluded / facing away simply receives nothing.
 */
export interface FlashBlindMsg {
  /** Peak white-out opacity, 0..1. */
  peak: number;
  /** Full-white hold before the fade starts (ms). */
  holdMs: number;
  /** Fade-out tail (ms). */
  fadeMs: number;
}

// ─────────────────────────────────────────────────────────────────────────
// Drop / pickup — CS:GO-style weapon swap on the ground.
// ─────────────────────────────────────────────────────────────────────────

/** Client → server: drop the currently equipped weapon (or a specific one). */
export interface DropWeaponMsg {
  /** Optional override — defaults to the player's currently equipped weapon. */
  weaponId?: string;
}

/** Client → server: try to pick up whichever ground item is under the crosshair. */
export interface PickupWeaponMsg {
  /** Server raycasts from the player's eye; this is informational only. */
  hintId?: number;
}

/**
 * Per-tick broadcast of every droppable weapon currently on the ground.
 * Server is authoritative — physics (gravity / floor settle) is simulated
 * server-side and clients lerp toward the snapshot to keep things cheap.
 */
export interface GroundItemSnapshot {
  id: number;
  weaponId: string;
  x: number;
  y: number;
  z: number;
  /** Yaw in degrees — kept around so the dropped weapon doesn't snap
   *  orientation each tick. */
  yaw: number;
  /** True once friction + floor settled it; clients can skip lerping. */
  resting: boolean;
  /** Preserved ammo state when a gun is dropped (matters for pickup). */
  magAmmo?: number;
  reserveAmmo?: number;
}

export interface GroundItemsStateBroadcastMsg {
  tick: number;
  items: GroundItemSnapshot[];
}
